// FolderPicker — modal that lets the user pick a folder for one or many
// designs. Doubles as the "manage folders" surface (rename, delete, create
// new). Per spec §13.4 (Folders) + §18.3 (Modal patterns).
//
// Usage:
//   <FolderPicker
//     open
//     mode="move"
//     designIds={['…', '…']}
//     onClose={() => setOpen(false)}
//     onMoved={() => qc.invalidateQueries({ queryKey: ['designs'] })}
//   />
//
// Two modes:
//   - "move"    — click a folder row to move the selected designs there.
//                 "No folder" detaches members.
//   - "manage"  — same list but each row has rename + delete affordances,
//                 and there's a "+ New folder" form at the top.

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Folder as FolderIcon, FolderPlus, Pencil, Trash2, X } from 'lucide-react';
import {
  bulkMove,
  createFolder,
  deleteFolder,
  listFolders,
  patchFolder,
  StudioApiError,
} from '../lib/api';
import type { Folder } from '../lib/types';
import { qk } from '../lib/queryClient';
import { useStudio } from '../lib/store';
import { ConfirmDialog } from './ConfirmDialog';

interface FolderPickerProps {
  open: boolean;
  onClose: () => void;
  /**
   * "move"   — pick a folder to move design IDs into
   * "manage" — rename / delete / create folders. designIds may be empty.
   */
  mode: 'move' | 'manage';
  designIds?: string[];
  /** Called after a successful move. */
  onMoved?: (folderId: string | null) => void;
}

export function FolderPicker({
  open,
  onClose,
  mode,
  designIds = [],
  onMoved,
}: FolderPickerProps) {
  const qc = useQueryClient();
  const pushToast = useStudio((s) => s.pushToast);

  const [newName, setNewName] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<Folder | null>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const foldersQ = useQuery({
    queryKey: qk.folders,
    queryFn: listFolders,
    enabled: open,
  });

  const folders = (foldersQ.data?.folders ?? []) as Folder[];
  // Sort: non-system folders first by sort_order, system folders last.
  const sorted = [...folders].sort((a, b) => {
    if (a.is_system !== b.is_system) return a.is_system ? 1 : -1;
    return a.sort_order - b.sort_order;
  });

  const moveMut = useMutation({
    mutationFn: async (folderId: string | null) => bulkMove(designIds, folderId),
    onSuccess: (_resp, folderId) => {
      pushToast({
        type: 'success',
        message:
          designIds.length === 1
            ? folderId
              ? 'Moved to folder'
              : 'Removed from folder'
            : `Moved ${designIds.length} designs`,
      });
      void qc.invalidateQueries({ queryKey: ['designs'] });
      void qc.invalidateQueries({ queryKey: qk.folders });
      onMoved?.(folderId);
      onClose();
    },
    onError: (err) => {
      pushToast({
        type: 'error',
        message: err instanceof StudioApiError ? err.message : 'Could not move designs',
      });
    },
  });

  const createMut = useMutation({
    mutationFn: async (name: string) => createFolder({ name }),
    onSuccess: () => {
      setNewName('');
      void qc.invalidateQueries({ queryKey: qk.folders });
      pushToast({ type: 'success', message: 'Folder created' });
    },
    onError: (err) => {
      pushToast({
        type: 'error',
        message: err instanceof StudioApiError ? err.message : 'Could not create folder',
      });
    },
  });

  const renameMut = useMutation({
    mutationFn: async (args: { id: string; name: string }) =>
      patchFolder(args.id, { name: args.name }),
    onSuccess: () => {
      setEditingId(null);
      setEditingName('');
      void qc.invalidateQueries({ queryKey: qk.folders });
    },
    onError: (err) => {
      pushToast({
        type: 'error',
        message: err instanceof StudioApiError ? err.message : 'Could not rename folder',
      });
    },
  });

  const deleteMut = useMutation({
    mutationFn: async (id: string) => deleteFolder(id),
    onSuccess: () => {
      setConfirmDelete(null);
      void qc.invalidateQueries({ queryKey: qk.folders });
      void qc.invalidateQueries({ queryKey: ['designs'] });
      pushToast({ type: 'success', message: 'Folder deleted' });
    },
    onError: (err) => {
      pushToast({
        type: 'error',
        message: err instanceof StudioApiError ? err.message : 'Could not delete folder',
      });
    },
  });

  if (!open) return null;
  return (
    <>
      <div className="modal-backdrop" role="presentation" onClick={onClose}>
        <div
          className="modal modal--md folder-picker"
          role="dialog"
          aria-modal="true"
          aria-label={mode === 'move' ? 'Move to folder' : 'Manage folders'}
          onClick={(e) => e.stopPropagation()}
        >
          <header className="modal__header">
            <h3>
              {mode === 'move'
                ? designIds.length === 1
                  ? 'Move to folder'
                  : `Move ${designIds.length} designs`
                : 'Manage folders'}
            </h3>
            <button type="button" className="modal__close" onClick={onClose} aria-label="Close">
              <X size={18} strokeWidth={1.8} />
            </button>
          </header>

          <div className="modal__body">
            {mode === 'manage' && (
              <form
                className="folder-picker__new"
                onSubmit={(e) => {
                  e.preventDefault();
                  const name = newName.trim();
                  if (!name) return;
                  createMut.mutate(name);
                }}
              >
                <FolderPlus size={18} aria-hidden="true" strokeWidth={1.6} />
                <input
                  type="text"
                  placeholder="New folder name"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  maxLength={80}
                  aria-label="New folder name"
                />
                <button
                  type="submit"
                  className="btn btn--primary btn--sm"
                  disabled={!newName.trim() || createMut.isPending}
                >
                  {createMut.isPending ? 'Creating…' : 'Create'}
                </button>
              </form>
            )}

            {foldersQ.isLoading ? (
              <div className="folder-picker__loading">Loading folders…</div>
            ) : sorted.length === 0 && mode === 'manage' ? (
              <div className="folder-picker__empty">
                You don&rsquo;t have any folders yet. Create one above to start organizing.
              </div>
            ) : (
              <ul className="folder-picker__list" role="list">
                {/* "No folder" row only for move mode */}
                {mode === 'move' && (
                  <li>
                    <button
                      type="button"
                      className="folder-picker__row"
                      onClick={() => moveMut.mutate(null)}
                      disabled={moveMut.isPending}
                    >
                      <FolderIcon
                        size={20}
                        aria-hidden="true"
                        strokeWidth={1.4}
                        style={{ opacity: 0.4 }}
                      />
                      <span className="folder-picker__name">No folder</span>
                      <span className="folder-picker__hint">Detach from any folder</span>
                    </button>
                  </li>
                )}
                {sorted.map((f) => {
                  const isEditing = editingId === f.id;
                  return (
                    <li key={f.id}>
                      {isEditing ? (
                        <form
                          className="folder-picker__row folder-picker__row--editing"
                          onSubmit={(e) => {
                            e.preventDefault();
                            const name = editingName.trim();
                            if (!name) return;
                            renameMut.mutate({ id: f.id, name });
                          }}
                        >
                          <FolderIcon size={20} aria-hidden="true" strokeWidth={1.4} />
                          <input
                            type="text"
                            value={editingName}
                            onChange={(e) => setEditingName(e.target.value)}
                            autoFocus
                            maxLength={80}
                            aria-label="Folder name"
                          />
                          <button
                            type="submit"
                            className="btn btn--primary btn--sm"
                            disabled={renameMut.isPending}
                          >
                            Save
                          </button>
                          <button
                            type="button"
                            className="btn btn--ghost btn--sm"
                            onClick={() => {
                              setEditingId(null);
                              setEditingName('');
                            }}
                          >
                            Cancel
                          </button>
                        </form>
                      ) : (
                        <div className="folder-picker__row">
                          <button
                            type="button"
                            className="folder-picker__row-main"
                            onClick={() => {
                              if (mode === 'move') moveMut.mutate(f.id);
                            }}
                            disabled={moveMut.isPending || mode === 'manage'}
                          >
                            <FolderIcon size={20} aria-hidden="true" strokeWidth={1.4} />
                            <span className="folder-picker__name">{f.name}</span>
                            <span className="folder-picker__count">
                              {f.design_count} design{f.design_count === 1 ? '' : 's'}
                            </span>
                            {f.is_system && <span className="folder-picker__badge">System</span>}
                          </button>
                          {mode === 'manage' && !f.is_system && (
                            <div className="folder-picker__actions">
                              <button
                                type="button"
                                className="folder-picker__icon-btn"
                                aria-label={`Rename ${f.name}`}
                                onClick={() => {
                                  setEditingId(f.id);
                                  setEditingName(f.name);
                                }}
                              >
                                <Pencil size={16} strokeWidth={1.6} />
                              </button>
                              <button
                                type="button"
                                className="folder-picker__icon-btn folder-picker__icon-btn--danger"
                                aria-label={`Delete ${f.name}`}
                                onClick={() => setConfirmDelete(f)}
                              >
                                <Trash2 size={16} strokeWidth={1.6} />
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <footer className="modal__footer">
            <button type="button" className="btn btn--ghost" onClick={onClose}>
              Close
            </button>
          </footer>
        </div>
      </div>

      <ConfirmDialog
        open={!!confirmDelete}
        title={`Delete "${confirmDelete?.name ?? ''}"?`}
        body={
          <>
            <p>
              The folder will be deleted but the {confirmDelete?.design_count ?? 0}{' '}
              design{confirmDelete?.design_count === 1 ? '' : 's'} inside will stay in your library
              (they&rsquo;ll just no longer be in this folder).
            </p>
            <p style={{ marginTop: 8, fontSize: '0.88rem', color: 'var(--ink-soft)' }}>
              This can&rsquo;t be undone.
            </p>
          </>
        }
        confirmLabel="Delete folder"
        destructive
        busy={deleteMut.isPending}
        onCancel={() => setConfirmDelete(null)}
        onConfirm={() => {
          if (confirmDelete) deleteMut.mutate(confirmDelete.id);
        }}
      />
    </>
  );
}
